import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Title from '../components/Title';
import SubmitButton from '../components/SubmitButton';
import NavigationStatus from '../components/NavigationStatus';
import useGtmEventTracking from '../components/useGtmEventTracking';
import { displayLoginName } from '../components/user/DisplayLoginName';
import UserContext from '../contexts/UserContext';

interface RemovalQuestions {
  floor: string;
  hasLift: boolean | null;
  hasFurniture: boolean | null;
  hasDisassembly: boolean | null;
  hasParking: boolean | null;
}

interface RemovalTier {
  id: string;
  name: string;
  price: number;
  description: string;
  imageUrl: string;
  details: string;
}

function RemovalsQuestionsComponent(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const gtmEventTracking = useGtmEventTracking();
  const { user } = useContext(UserContext);

  // Get the selected tier from location state
  const selectedTier = (location.state as any)?.selectedTier as RemovalTier;

  const [questions, setQuestions] = useState<RemovalQuestions>({
    floor: '',
    hasLift: null,
    hasFurniture: null,
    hasDisassembly: null,
    hasParking: null,
  });

  const [showTooltip, setShowTooltip] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    gtmEventTracking({ event: 'booking_step_removals_questions' });
  }, [gtmEventTracking]);

  const handleQuestionChange = (field: keyof RemovalQuestions, value: any) => {
    setQuestions(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Reset disassembly if furniture is set to false
    if (field === 'hasFurniture' && value === false) {
      setQuestions(prev => ({
        ...prev,
        hasFurniture: false,
        hasDisassembly: null
      }));
    }
  };

  const isFormValid = () => {
    const floorSelected = questions.floor !== '';
    const liftAnswered = questions.floor === '0' || questions.hasLift !== null;
    const disassemblyAnswered = questions.hasFurniture === false || questions.hasDisassembly !== null;
    return floorSelected && 
           liftAnswered &&
           questions.hasFurniture !== null &&
           disassemblyAnswered &&
           questions.hasParking !== null;
  };

  const handleContinue = () => {
    if (isFormValid()) {
      // Navigate to extras page with all data
      history.push('/removals/extras', {
        selectedTier,
        questions,
        boxes: [], // Removals don't use boxes
        removalService: true
      });
    }
  };

  const floorOptions = [
    { value: '0', label: 'Ground floor' },
    { value: '1', label: '1st floor' },
    { value: '2', label: '2nd floor' },
    { value: '3', label: '3rd floor' },
    { value: '4', label: '4th floor' },
    { value: '5', label: '5th floor' },
    { value: '6', label: '6th floor' },
    { value: '7', label: '7th floor' },
    { value: '8', label: '8th floor' },
    { value: '9', label: '9th floor' },
    { value: '10', label: '10th floor' },
    { value: '11', label: '11th floor' },
    { value: '12', label: '12th floor' },
    { value: '13', label: '13th floor' },
    { value: '14', label: '14th floor' },
    { value: '15', label: '15th floor' },
    { value: '16', label: '16th floor' },
    { value: '17', label: '17th floor' },
    { value: '18', label: '18th floor' },
    { value: '19', label: '19th floor' },
    { value: '20', label: '20th+ floor' },
  ];

  if (!selectedTier) {
    // Redirect back to removals page if no tier selected
    history.replace('/removals');
    return <div>Redirecting...</div>;
  }

  return (
    <>
      <section className="login-status-bar">
        <div className="status-bar-content">
          <p className="grey">{displayLoginName(user)}</p>
        </div>
      </section>
      <NavigationStatus 
        step={2} 
        locationState={{ selectedTier }}
      />

      <div className="removals-questions-container">
        <Title>Tell us more about your move</Title>
        <p className="grey text-center mb-4">
          Help us provide you with the most accurate quote for your {selectedTier.name.toLowerCase()} removal.
        </p>

        <div className="form">
          <div id="removals-funnel-wrapper" className="sections questions mt-n3 mt-md-0">
            <h2 className="ls-bold text-center mb-4">
              <span>What type of property are you moving out of?</span>
            </h2>

            {/* Floor Dropdown */}
            <div className="open transparent dropdown mb-4">
              <div className="card">
                <div className="card-header dropdown-header">
                  <div className="header-title">What floor is it on?</div>
                </div>
                <div className="dropdown-body">
                  <div className="text-start ls-grey1 mb-3">
                    If you're moving from a house, please select 'Ground floor'.
                  </div>
                  <div className="dropdown-content">
                    <div className="form-group col-lg-6 p-0 my-3">
                      <div className="inline-label ls-select form-group">
                        <select
                          className="form-select mb-0 form-control"
                          value={questions.floor}
                          onChange={(e) => handleQuestionChange('floor', e.target.value)}
                        >
                          <option value="">Please select *</option>
                          {floorOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lift Question - Only show if floor is selected and not ground floor */}
            {questions.floor !== '' && questions.floor !== '0' && (
              <div className="open transparent dropdown mb-4">
                <div className="card">
                  <div className="card-header dropdown-header">
                    <div className="header-title">Does your property have a lift?</div>
                  </div>
                  <div className="dropdown-body">
                    <div className="dropdown-content">
                      <div 
                        className={`ls-radio-button ${questions.hasLift === true ? 'selected' : ''}`}
                        onClick={() => handleQuestionChange('hasLift', true)}
                      >
                        <div className="marker"></div>
                        <div className="text text-start">Yes</div>
                      </div>
                      <div 
                        className={`ls-radio-button ${questions.hasLift === false ? 'selected' : ''}`}
                        onClick={() => handleQuestionChange('hasLift', false)}
                      >
                        <div className="marker"></div>
                        <div className="text text-start">No</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Furniture Question */}
            <div className="open transparent dropdown mb-4">
              <div className="card">
                <div className="card-header dropdown-header">
                  <div className="header-title">
                    Do you need us to move any furniture or large items which require 2 people to carry them?
                    <span 
                      className="info-icon-wrapper"
                      onMouseEnter={() => setShowTooltip('furniture')}
                      onMouseLeave={() => setShowTooltip(null)}
                      style={{ position: 'relative', marginLeft: '8px' }}
                    >
                      <svg 
                        aria-hidden="true" 
                        className="svg-inline--fa info-icon" 
                        focusable="false" 
                        role="img" 
                        xmlns="http://www.w3.org/2000/svg" 
                        viewBox="0 0 512 512"
                        width="24"
                        height="24"
                      >
                        <path 
                          fill="currentColor" 
                          d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336l24 0 0-64-24 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l48 0c13.3 0 24 10.7 24 24l0 88 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-80 0c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"
                        />
                      </svg>
                      {showTooltip === 'furniture' && (
                        <div className="info-tooltip">
                          <p>This includes items such as tables, beds, and sofas.</p>
                        </div>
                      )}
                    </span>
                  </div>
                </div>
                <div className="dropdown-body">
                  <div className="dropdown-content">
                    <div 
                      className={`ls-radio-button ${questions.hasFurniture === true ? 'selected' : ''}`}
                      onClick={() => handleQuestionChange('hasFurniture', true)}
                    >
                      <div className="marker"></div>
                      <div className="text text-start">Yes</div>
                    </div>
                    <div 
                      className={`ls-radio-button ${questions.hasFurniture === false ? 'selected' : ''}`}
                      onClick={() => handleQuestionChange('hasFurniture', false)}
                    >
                      <div className="marker"></div>
                      <div className="text text-start">No</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Furniture Disassembly Question - Only show if hasFurniture is true */}
            {questions.hasFurniture === true && (
              <div className="open transparent dropdown mb-4">
                <div className="card">
                  <div className="card-header dropdown-header">
                    <div className="header-title">Would you like us to disassemble any furniture?</div>
                  </div>
                  <div className="dropdown-body">
                    <div className="text-start ls-grey1 mb-3">
                      <strong>Note:</strong> Our furniture disassembly service costs £79.
                    </div>
                    <div className="dropdown-content">
                      <div 
                        className={`ls-radio-button ${questions.hasDisassembly === true ? 'selected' : ''}`}
                        onClick={() => handleQuestionChange('hasDisassembly', true)}
                      >
                        <div className="marker"></div>
                        <div className="text text-start">Yes</div>
                      </div>
                      <div 
                        className={`ls-radio-button ${questions.hasDisassembly === false ? 'selected' : ''}`}
                        onClick={() => handleQuestionChange('hasDisassembly', false)}
                      >
                        <div className="marker"></div>
                        <div className="text text-start">No</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Parking Question */}
            <div className="open transparent dropdown mb-4">
              <div className="card">
                <div className="card-header dropdown-header">
                  <div className="header-title">
                    Does your property have parking?
                    <span 
                      className="info-icon-wrapper"
                      onMouseEnter={() => setShowTooltip('parking')}
                      onMouseLeave={() => setShowTooltip(null)}
                      style={{ position: 'relative', marginLeft: '8px' }}
                    >
                      <svg 
                        aria-hidden="true" 
                        className="svg-inline--fa info-icon" 
                        focusable="false" 
                        role="img" 
                        xmlns="http://www.w3.org/2000/svg" 
                        viewBox="0 0 512 512"
                        width="24"
                        height="24"
                      >
                        <path 
                          fill="currentColor" 
                          d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336l24 0 0-64-24 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l48 0c13.3 0 24 10.7 24 24l0 88 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-80 0c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"
                        />
                      </svg>
                      {showTooltip === 'parking' && (
                        <div className="info-tooltip">
                          <p>Please note that if you do not have appropriate parking available, we may not be able to collect your stuff.</p>
                          <p>We recommend obtaining any parking permits needed in advance.</p>
                          <p>Where required, we will pass on any parking costs we incur during your move on to you.</p>
                        </div>
                      )}
                    </span>
                  </div>
                </div>
                <div className="dropdown-body">
                  <div className="dropdown-content">
                    <div className="text-start ls-grey1 mb-3">
                      Our moving team will need to park on your moving day.
                    </div>
                    <div 
                      className={`ls-radio-button ${questions.hasParking === true ? 'selected' : ''}`}
                      onClick={() => handleQuestionChange('hasParking', true)}
                    >
                      <div className="marker"></div>
                      <div className="text text-start">Yes</div>
                    </div>
                    <div 
                      className={`ls-radio-button ${questions.hasParking === false ? 'selected' : ''}`}
                      onClick={() => handleQuestionChange('hasParking', false)}
                    >
                      <div className="marker"></div>
                      <div className="text text-start">No</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div id="funnelFooter" className="mt-3">
            <div className="sticky-bar">
              <div className="sticky-bar-limit"></div>
              <div className="text-center sticky-bar-body">
                <SubmitButton 
                  disabled={!isFormValid()}
                  onClick={handleContinue}
                >
                  Continue
                </SubmitButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default RemovalsQuestionsComponent;
